/**
 * audioEditorHooks.js - React Hooks for DAW Audio Editing
 * 
 * Custom hooks for:
 * - Audio file loading and playback
 * - Effect processing with undo/redo
 * - Waveform visualization
 * - Loop machine
 * - Beat generation
 * - Real-time audio analysis
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  getAudioContext,
  ensureAudioContextRunning,
  loadAudioFromUrl,
  cloneBuffer,
  sliceBuffer,
  concatBuffers,
  mixBuffers,
  timeStretch,
  changeSpeed,
  pitchShift,
  autoTune,
  applyReverb,
  applyDelay,
  applyChorus,
  applyFilter,
  applyEQ,
  applyNoiseGate,
  applyCompression,
  applyLimiter,
  applyGain,
  normalize,
  applyNoiseReduction,
  extractVocals,
  removeVocals,
  fadeIn,
  fadeOut,
  crossfade,
  createLoop,
  repeatBuffer,
  createSeamlessLoop,
  generateBeat,
  generateKick,
  generateSnare,
  generateHihat,
  calculateRMS,
  calculatePeak,
  generateWaveformData,
  createEffectChain,
  bufferToWav,
  downloadAsWav,
  detectBPM,
  AUTOTUNE_COLORS,
  SCALES,
  BEAT_PATTERNS,
  FADE_CURVES,
  EFFECT_PRESETS,
  pipe,
  compose,
  Maybe
} from './audioFP';

// =============================================================================
// CORE AUDIO EDITOR HOOK
// =============================================================================

/**
 * Main hook for audio editing with undo/redo support
 */
export const useAudioEditor = (initialBuffer = null) => {
  const [buffer, setBuffer] = useState(initialBuffer);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Push current state to history before modification
  const pushHistory = useCallback(() => {
    if (!buffer) return;
    
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(cloneBuffer(buffer));
      // Limit history size
      if (newHistory.length > 50) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [buffer, historyIndex]);
  
  // Apply an effect function to the buffer
  const applyEffect = useCallback(async (effectFn, description = 'effect') => {
    if (!buffer) {
      setError('No audio loaded');
      return null;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      pushHistory();
      const result = await Promise.resolve(effectFn(buffer));
      setBuffer(result);
      return result;
    } catch (err) {
      setError(`Failed to apply ${description}: ${err.message}`);
      return null;
    } finally {
      setIsProcessing(false);
    }
  }, [buffer, pushHistory]);
  
  // Undo last change
  const undo = useCallback(() => {
    if (historyIndex < 0) return;
    
    if (historyIndex === history.length - 1 && buffer) {
      // Save current state for redo
      setHistory(prev => [...prev, cloneBuffer(buffer)]);
    }
    
    setBuffer(cloneBuffer(history[historyIndex]));
    setHistoryIndex(prev => prev - 1);
  }, [buffer, history, historyIndex]);
  
  // Redo
  const redo = useCallback(() => {
    if (historyIndex >= history.length - 2) return;
    
    setHistoryIndex(prev => prev + 1);
    setBuffer(cloneBuffer(history[historyIndex + 2]));
  }, [history, historyIndex]);
  
  // Load audio from file
  const loadFromFile = useCallback(async (file) => {
    setIsProcessing(true);
    setError(null);
    
    try {
      await ensureAudioContextRunning();
      const arrayBuffer = await file.arrayBuffer();
      const ctx = getAudioContext();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      
      setBuffer(audioBuffer);
      setHistory([]);
      setHistoryIndex(-1);
      
      return audioBuffer;
    } catch (err) {
      setError(`Failed to load audio: ${err.message}`);
      return null;
    } finally {
      setIsProcessing(false);
    }
  }, []);
  
  // Load audio from URL
  const loadFromUrl = useCallback(async (url) => {
    setIsProcessing(true);
    setError(null);
    
    try {
      await ensureAudioContextRunning();
      const audioBuffer = await loadAudioFromUrl(url).toPromise();
      
      setBuffer(audioBuffer);
      setHistory([]);
      setHistoryIndex(-1);
      
      return audioBuffer;
    } catch (err) {
      setError(`Failed to load audio: ${err.message}`);
      return null;
    } finally {
      setIsProcessing(false);
    }
  }, []);
  
  // Clear audio
  const clear = useCallback(() => {
    setBuffer(null);
    setHistory([]);
    setHistoryIndex(-1);
    setError(null);
  }, []);
  
  // Export as WAV
  const exportWav = useCallback((filename = 'audio.wav') => {
    if (!buffer) {
      setError('No audio to export');
      return;
    }
    downloadAsWav(buffer, filename);
  }, [buffer]);
  
  // Get WAV blob
  const getWavBlob = useCallback(() => {
    if (!buffer) return null;
    return bufferToWav(buffer);
  }, [buffer]);
  
  return {
    buffer,
    setBuffer,
    isProcessing,
    error,
    canUndo: historyIndex >= 0,
    canRedo: historyIndex < history.length - 2,
    
    // Actions
    applyEffect,
    undo,
    redo,
    loadFromFile,
    loadFromUrl,
    clear,
    exportWav,
    getWavBlob
  };
};

// =============================================================================
// PLAYBACK HOOK
// =============================================================================

/**
 * Hook for audio playback with transport controls
 */
export const useAudioPlayback = (buffer) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loop, setLoop] = useState(false);
  const [loopStart, setLoopStart] = useState(0);
  const [loopEnd, setLoopEnd] = useState(0);
  
  const sourceRef = useRef(null);
  const startTimeRef = useRef(0);
  const pauseTimeRef = useRef(0);
  const animationRef = useRef(null);
  
  // Update duration when buffer changes
  useEffect(() => {
    if (buffer) {
      setDuration(buffer.length / buffer.sampleRate);
      setLoopEnd(buffer.length / buffer.sampleRate);
    } else {
      setDuration(0);
      setLoopEnd(0);
    }
  }, [buffer]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sourceRef.current) {
        try { sourceRef.current.stop(); } catch (e) {}
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  // Update current time during playback
  const updateTime = useCallback(() => {
    if (!isPlaying) return;
    
    const ctx = getAudioContext();
    const elapsed = ctx.currentTime - startTimeRef.current + pauseTimeRef.current;
    
    if (loop && elapsed >= loopEnd) {
      // Handle loop
      const loopDuration = loopEnd - loopStart;
      setCurrentTime(loopStart + ((elapsed - loopStart) % loopDuration));
    } else if (elapsed >= duration) {
      // Playback complete
      setIsPlaying(false);
      setCurrentTime(0);
      pauseTimeRef.current = 0;
      return;
    } else {
      setCurrentTime(elapsed);
    }
    
    animationRef.current = requestAnimationFrame(updateTime);
  }, [isPlaying, duration, loop, loopStart, loopEnd]);
  
  // Start playback
  const play = useCallback(async (startPosition = null) => {
    if (!buffer) return;
    
    await ensureAudioContextRunning();
    const ctx = getAudioContext();
    
    // Stop any existing playback
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch (e) {}
    }
    
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = loop;
    
    if (loop) {
      source.loopStart = loopStart;
      source.loopEnd = loopEnd;
    }
    
    source.connect(ctx.destination);
    sourceRef.current = source;
    
    // Ensure offset is a finite, valid number
    let offset = startPosition !== null ? startPosition : pauseTimeRef.current;
    if (!Number.isFinite(offset) || offset < 0) {
      offset = 0;
    }
    // Clamp to buffer duration
    const maxOffset = buffer.duration || 0;
    if (offset > maxOffset) {
      offset = 0;
    }
    
    startTimeRef.current = ctx.currentTime;
    pauseTimeRef.current = offset;
    
    source.start(0, offset);
    setIsPlaying(true);
    
    source.onended = () => {
      if (!loop) {
        setIsPlaying(false);
        setCurrentTime(0);
        pauseTimeRef.current = 0;
      }
    };
    
    animationRef.current = requestAnimationFrame(updateTime);
  }, [buffer, loop, loopStart, loopEnd, updateTime]);
  
  // Pause playback
  const pause = useCallback(() => {
    if (!isPlaying) return;
    
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch (e) {}
    }
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    const ctx = getAudioContext();
    pauseTimeRef.current = ctx.currentTime - startTimeRef.current + pauseTimeRef.current;
    setIsPlaying(false);
  }, [isPlaying]);
  
  // Stop playback
  const stop = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch (e) {}
    }
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    setIsPlaying(false);
    setCurrentTime(0);
    pauseTimeRef.current = 0;
  }, []);
  
  // Seek to position
  const seek = useCallback((time) => {
    pauseTimeRef.current = Math.max(0, Math.min(time, duration));
    setCurrentTime(pauseTimeRef.current);
    
    if (isPlaying) {
      play(pauseTimeRef.current);
    }
  }, [duration, isPlaying, play]);
  
  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, pause, play]);
  
  // Set loop region
  const setLoopRegion = useCallback((start, end) => {
    setLoopStart(Math.max(0, start));
    setLoopEnd(Math.min(duration, end));
  }, [duration]);
  
  return {
    isPlaying,
    currentTime,
    duration,
    loop,
    loopStart,
    loopEnd,
    
    // Actions
    play,
    pause,
    stop,
    seek,
    togglePlayPause,
    setLoop,
    setLoopRegion
  };
};

// =============================================================================
// WAVEFORM VISUALIZATION HOOK
// =============================================================================

/**
 * Hook for generating and managing waveform display data
 */
export const useWaveform = (buffer, width = 800) => {
  const [waveformData, setWaveformData] = useState(null);
  const [peaks, setPeaks] = useState({ rms: 0, peak: 0 });
  
  useEffect(() => {
    if (!buffer) {
      setWaveformData(null);
      setPeaks({ rms: 0, peak: 0 });
      return;
    }
    
    // Generate waveform data
    const data = generateWaveformData(width, buffer);
    setWaveformData(data);
    
    // Calculate peaks
    setPeaks({
      rms: calculateRMS(buffer),
      peak: calculatePeak(buffer)
    });
  }, [buffer, width]);
  
  return { waveformData, peaks };
};

// =============================================================================
// EFFECT PROCESSOR HOOK
// =============================================================================

/**
 * Hook for managing effect parameters and application
 */
export const useEffectProcessor = (applyEffect) => {
  // Tempo effects
  const stretch = useCallback((factor) => 
    applyEffect(timeStretch(factor), 'time stretch'), [applyEffect]);
  
  const speed = useCallback((factor) => 
    applyEffect(changeSpeed(factor), 'speed change'), [applyEffect]);
  
  // Pitch effects  
  const pitch = useCallback((semitones) => 
    applyEffect(pitchShift(semitones), 'pitch shift'), [applyEffect]);
  
  const tune = useCallback((scale, rootNote, color) => 
    applyEffect(autoTune(scale, rootNote, color), 'auto-tune'), [applyEffect]);
  
  // Spatial effects
  const reverb = useCallback((roomSize, damping, wetMix) => 
    applyEffect(applyReverb(roomSize, damping, wetMix), 'reverb'), [applyEffect]);
  
  const delay = useCallback((time, feedback, wetMix) => 
    applyEffect(applyDelay(time, feedback, wetMix), 'delay'), [applyEffect]);
  
  const chorus = useCallback((depth, rate, wetMix) => 
    applyEffect(applyChorus(depth, rate, wetMix), 'chorus'), [applyEffect]);
  
  // Filter effects
  const filter = useCallback((type, frequency, Q, gain) => 
    applyEffect(applyFilter(type, frequency, Q, gain), 'filter'), [applyEffect]);
  
  const eq = useCallback((bands) => 
    applyEffect(applyEQ(bands), 'EQ'), [applyEffect]);
  
  const noiseGate = useCallback((threshold, attack, release) => 
    applyEffect(applyNoiseGate(threshold, attack, release), 'noise gate'), [applyEffect]);
  
  // Dynamics
  const compress = useCallback((threshold, ratio, attack, release, makeupGain) => 
    applyEffect(applyCompression(threshold, ratio, attack, release, makeupGain), 'compression'), 
    [applyEffect]);
  
  const limit = useCallback((ceiling) => 
    applyEffect(applyLimiter(ceiling), 'limiter'), [applyEffect]);
  
  const gain = useCallback((db) => 
    applyEffect(applyGain(db), 'gain'), [applyEffect]);
  
  const norm = useCallback((targetDb) => 
    applyEffect(normalize(targetDb), 'normalize'), [applyEffect]);
  
  // Noise/Vocal
  const noiseReduce = useCallback((threshold, smoothing) => 
    applyEffect(applyNoiseReduction(threshold, smoothing), 'noise reduction'), [applyEffect]);
  
  const isolateVocals = useCallback(() => 
    applyEffect(extractVocals, 'vocal isolation'), [applyEffect]);
  
  const removeVox = useCallback(() => 
    applyEffect(removeVocals, 'vocal removal'), [applyEffect]);
  
  // Fades
  const fadeInEffect = useCallback((duration, curve = 'linear') => 
    applyEffect(fadeIn(duration, curve), 'fade in'), [applyEffect]);
  
  const fadeOutEffect = useCallback((duration, curve = 'linear') => 
    applyEffect(fadeOut(duration, curve), 'fade out'), [applyEffect]);
  
  // Apply preset chain
  const applyPreset = useCallback((presetName) => {
    const preset = EFFECT_PRESETS[presetName];
    if (preset) {
      return applyEffect(preset, `${presetName} preset`);
    }
  }, [applyEffect]);
  
  return {
    // Tempo
    stretch,
    speed,
    
    // Pitch
    pitch,
    tune,
    
    // Spatial
    reverb,
    delay,
    chorus,
    
    // Filters
    filter,
    eq,
    noiseGate,
    
    // Dynamics
    compress,
    limit,
    gain,
    norm,
    
    // Noise/Vocal
    noiseReduce,
    isolateVocals,
    removeVox,
    
    // Fades
    fadeIn: fadeInEffect,
    fadeOut: fadeOutEffect,
    
    // Presets
    applyPreset,
    
    // Available options
    autotuneColors: AUTOTUNE_COLORS,
    scales: SCALES,
    fadeCurves: FADE_CURVES,
    presets: Object.keys(EFFECT_PRESETS)
  };
};

// =============================================================================
// LOOP MACHINE HOOK
// =============================================================================

/**
 * Hook for loop-based audio manipulation
 */
export const useLoopMachine = (buffer, applyEffect) => {
  const [loops, setLoops] = useState([]);
  const [activeLoop, setActiveLoop] = useState(null);
  
  // Create a loop from region
  const createLoopRegion = useCallback((startTime, endTime, name = 'Loop') => {
    if (!buffer) return null;
    
    const loopBuffer = sliceBuffer(startTime, endTime, buffer);
    const newLoop = {
      id: `loop-${Date.now()}`,
      name,
      buffer: loopBuffer,
      startTime,
      endTime,
      duration: endTime - startTime
    };
    
    setLoops(prev => [...prev, newLoop]);
    return newLoop;
  }, [buffer]);
  
  // Create seamless loop
  const createSeamless = useCallback((loopDuration, crossfadeDuration = 0.1) => {
    if (!buffer) return null;
    
    const seamless = createSeamlessLoop(loopDuration, crossfadeDuration, buffer);
    const newLoop = {
      id: `loop-${Date.now()}`,
      name: 'Seamless Loop',
      buffer: seamless,
      startTime: 0,
      endTime: loopDuration,
      duration: loopDuration,
      seamless: true
    };
    
    setLoops(prev => [...prev, newLoop]);
    return newLoop;
  }, [buffer]);
  
  // Repeat a loop
  const repeatLoop = useCallback((loopId, times) => {
    const loop = loops.find(l => l.id === loopId);
    if (!loop) return null;
    
    const repeated = repeatBuffer(times, loop.buffer);
    return repeated;
  }, [loops]);
  
  // Apply loop to main buffer
  const applyLoopToBuffer = useCallback((loopId, times = 1) => {
    const loop = loops.find(l => l.id === loopId);
    if (!loop) return;
    
    const repeated = repeatBuffer(times, loop.buffer);
    return applyEffect(() => repeated, 'apply loop');
  }, [loops, applyEffect]);
  
  // Delete loop
  const deleteLoop = useCallback((loopId) => {
    setLoops(prev => prev.filter(l => l.id !== loopId));
    if (activeLoop === loopId) {
      setActiveLoop(null);
    }
  }, [activeLoop]);
  
  // Clear all loops
  const clearLoops = useCallback(() => {
    setLoops([]);
    setActiveLoop(null);
  }, []);
  
  return {
    loops,
    activeLoop,
    setActiveLoop,
    
    // Actions
    createLoopRegion,
    createSeamless,
    repeatLoop,
    applyLoopToBuffer,
    deleteLoop,
    clearLoops
  };
};

// =============================================================================
// BEAT GENERATOR HOOK
// =============================================================================

/**
 * Hook for beat generation and drum pattern creation
 */
export const useBeatGenerator = () => {
  const [currentPattern, setCurrentPattern] = useState('basic4_4');
  const [bpm, setBpm] = useState(120);
  const [bars, setBars] = useState(4);
  const [generatedBeat, setGeneratedBeat] = useState(null);
  const [customPattern, setCustomPattern] = useState({
    kick: Array(16).fill(0),
    snare: Array(16).fill(0),
    hihat: Array(16).fill(0)
  });
  
  // Generate beat from preset pattern
  const generateFromPreset = useCallback((patternName = currentPattern) => {
    const beat = generateBeat(patternName, bpm, bars);
    setGeneratedBeat(beat);
    return beat;
  }, [currentPattern, bpm, bars]);
  
  // Generate beat from custom pattern
  const generateCustomBeat = useCallback(() => {
    // Build pattern in same format as presets
    const pattern = {
      kick: customPattern.kick,
      snare: customPattern.snare,
      hihat: customPattern.hihat
    };
    
    // Temporarily add to BEAT_PATTERNS
    const sampleRate = 44100;
    const stepsPerBar = 16;
    const totalSteps = stepsPerBar * bars;
    const stepDuration = 60 / bpm / 4;
    const totalDuration = totalSteps * stepDuration;
    
    const ctx = getAudioContext();
    const length = Math.floor(sampleRate * totalDuration);
    const result = ctx.createBuffer(2, length, sampleRate);
    
    const kick = generateKick(sampleRate);
    const snare = generateSnare(sampleRate);
    const hihat = generateHihat(sampleRate);
    
    for (let step = 0; step < totalSteps; step++) {
      const patternStep = step % stepsPerBar;
      const sampleOffset = Math.floor(step * stepDuration * sampleRate);
      
      if (pattern.kick[patternStep]) {
        mixSampleIntoBuffer(result, kick, sampleOffset, 1.0);
      }
      if (pattern.snare[patternStep]) {
        mixSampleIntoBuffer(result, snare, sampleOffset, 0.8);
      }
      if (pattern.hihat[patternStep]) {
        mixSampleIntoBuffer(result, hihat, sampleOffset, 0.4);
      }
    }
    
    setGeneratedBeat(result);
    return result;
  }, [customPattern, bpm, bars]);
  
  // Toggle step in custom pattern
  const toggleStep = useCallback((instrument, step) => {
    setCustomPattern(prev => ({
      ...prev,
      [instrument]: prev[instrument].map((v, i) => i === step ? (v ? 0 : 1) : v)
    }));
  }, []);
  
  // Clear custom pattern
  const clearCustomPattern = useCallback(() => {
    setCustomPattern({
      kick: Array(16).fill(0),
      snare: Array(16).fill(0),
      hihat: Array(16).fill(0)
    });
  }, []);
  
  // Copy preset to custom
  const loadPresetToCustom = useCallback((patternName) => {
    const pattern = BEAT_PATTERNS[patternName];
    if (pattern) {
      setCustomPattern({
        kick: [...pattern.kick],
        snare: [...pattern.snare],
        hihat: [...pattern.hihat]
      });
    }
  }, []);
  
  return {
    currentPattern,
    setCurrentPattern,
    bpm,
    setBpm,
    bars,
    setBars,
    generatedBeat,
    customPattern,
    
    // Available patterns
    patterns: Object.keys(BEAT_PATTERNS),
    
    // Actions
    generateFromPreset,
    generateCustomBeat,
    toggleStep,
    clearCustomPattern,
    loadPresetToCustom,
    setCustomPattern,
    generateAiPattern: useCallback(async () => {
      // Simulate AI generation (placeholder for ML backend)
      // In a real implementation, this would call apiService.generateDrumPattern()
      const instruments = ['kick', 'snare', 'hihat'];
      const newPattern = {
        kick: Array(16).fill(0),
        snare: Array(16).fill(0),
        hihat: Array(16).fill(0)
      };
      
      // Simple algorithmic generation
      for (let i = 0; i < 16; i++) {
        if (i % 4 === 0) newPattern.kick[i] = Math.random() > 0.2 ? 1 : 0;
        if (i % 8 === 4) newPattern.snare[i] = Math.random() > 0.2 ? 1 : 0;
        if (i % 2 === 0) newPattern.hihat[i] = Math.random() > 0.3 ? 1 : 0;
        
        // Add some "AI" randomness/syncopation
        if (Math.random() > 0.8) newPattern.kick[i] = 1;
        if (Math.random() > 0.9) newPattern.snare[i] = 1;
        if (Math.random() > 0.7) newPattern.hihat[i] = 1;
      }
      
      setCustomPattern(newPattern);
      return newPattern;
    }, [])
  };
};

// Helper for beat generation
const mixSampleIntoBuffer = (destBuffer, srcBuffer, offset, gain) => {
  for (let ch = 0; ch < destBuffer.numberOfChannels; ch++) {
    const dest = destBuffer.getChannelData(ch);
    const src = srcBuffer.getChannelData(Math.min(ch, srcBuffer.numberOfChannels - 1));
    
    for (let i = 0; i < src.length && offset + i < dest.length; i++) {
      dest[offset + i] += src[i] * gain;
    }
  }
};

// =============================================================================
// AUDIO ANALYSIS HOOK
// =============================================================================

/**
 * Hook for real-time audio analysis during playback
 */
export const useAudioAnalysis = (buffer, isPlaying) => {
  const [frequencies, setFrequencies] = useState(null);
  const [waveform, setWaveform] = useState(null);
  const [bpm, setBpm] = useState(null);
  
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  
  // Detect BPM when buffer changes
  useEffect(() => {
    if (buffer) {
      detectBPM(buffer).then(setBpm);
    } else {
      setBpm(null);
    }
  }, [buffer]);
  
  // Setup analyser node
  useEffect(() => {
    if (!isPlaying) {
      setFrequencies(null);
      setWaveform(null);
      return;
    }
    
    const ctx = getAudioContext();
    analyserRef.current = ctx.createAnalyser();
    analyserRef.current.fftSize = 2048;
    
    const bufferLength = analyserRef.current.frequencyBinCount;
    const freqData = new Uint8Array(bufferLength);
    const waveData = new Uint8Array(bufferLength);
    
    const analyze = () => {
      if (!analyserRef.current) return;
      
      analyserRef.current.getByteFrequencyData(freqData);
      analyserRef.current.getByteTimeDomainData(waveData);
      
      setFrequencies(new Uint8Array(freqData));
      setWaveform(new Uint8Array(waveData));
      
      animationRef.current = requestAnimationFrame(analyze);
    };
    
    analyze();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying]);
  
  return {
    frequencies,
    waveform,
    bpm,
    analyser: analyserRef.current
  };
};

// =============================================================================
// REGION SELECTION HOOK
// =============================================================================

/**
 * Hook for selecting regions in the waveform
 */
export const useRegionSelection = (duration) => {
  const [selection, setSelection] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  
  const startSelection = useCallback((time) => {
    setSelection({ start: time, end: time });
    setIsSelecting(true);
  }, []);
  
  const updateSelection = useCallback((time) => {
    if (!isSelecting) return;
    
    setSelection(prev => ({
      start: Math.min(prev.start, time),
      end: Math.max(prev.start, time)
    }));
  }, [isSelecting]);
  
  const endSelection = useCallback(() => {
    setIsSelecting(false);
  }, []);
  
  const clearSelection = useCallback(() => {
    setSelection(null);
    setIsSelecting(false);
  }, []);
  
  const selectAll = useCallback(() => {
    setSelection({ start: 0, end: duration });
  }, [duration]);
  
  // Selection duration
  const selectionDuration = useMemo(() => {
    if (!selection) return 0;
    return selection.end - selection.start;
  }, [selection]);
  
  return {
    selection,
    isSelecting,
    selectionDuration,
    
    // Actions
    startSelection,
    updateSelection,
    endSelection,
    clearSelection,
    selectAll
  };
};

// =============================================================================
// KEYBOARD SHORTCUTS HOOK
// =============================================================================

/**
 * Hook for DAW keyboard shortcuts
 */
export const useDAWKeyboardShortcuts = ({
  onPlay,
  onPause,
  onStop,
  onUndo,
  onRedo,
  onDelete,
  onCut,
  onCopy,
  onPaste,
  onSelectAll,
  onSave
}) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const isCmd = e.metaKey || e.ctrlKey;
      
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          if (e.shiftKey) {
            onStop?.();
          } else {
            onPlay?.();
          }
          break;
          
        case 'KeyZ':
          if (isCmd) {
            e.preventDefault();
            if (e.shiftKey) {
              onRedo?.();
            } else {
              onUndo?.();
            }
          }
          break;
          
        case 'KeyY':
          if (isCmd) {
            e.preventDefault();
            onRedo?.();
          }
          break;
          
        case 'KeyA':
          if (isCmd) {
            e.preventDefault();
            onSelectAll?.();
          }
          break;
          
        case 'KeyS':
          if (isCmd) {
            e.preventDefault();
            onSave?.();
          }
          break;
          
        case 'KeyX':
          if (isCmd) {
            e.preventDefault();
            onCut?.();
          }
          break;
          
        case 'KeyC':
          if (isCmd) {
            e.preventDefault();
            onCopy?.();
          }
          break;
          
        case 'KeyV':
          if (isCmd) {
            e.preventDefault();
            onPaste?.();
          }
          break;
          
        case 'Delete':
        case 'Backspace':
          if (!isCmd) {
            e.preventDefault();
            onDelete?.();
          }
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onPlay, onPause, onStop, onUndo, onRedo, onDelete, onCut, onCopy, onPaste, onSelectAll, onSave]);
};

// =============================================================================
// MULTITRACK HOOK
// =============================================================================

/**
 * Hook for managing multiple audio tracks
 */
export const useMultitrack = () => {
  const [tracks, setTracks] = useState([]);
  const [soloTrackId, setSoloTrackId] = useState(null);
  const [masterVolume, setMasterVolume] = useState(1);
  
  // Add track
  const addTrack = useCallback((buffer, name = 'Track') => {
    const track = {
      id: `track-${Date.now()}`,
      name,
      buffer,
      volume: 1,
      pan: 0,
      muted: false,
      solo: false,
      effects: []
    };
    
    setTracks(prev => [...prev, track]);
    return track;
  }, []);
  
  // Remove track
  const removeTrack = useCallback((trackId) => {
    setTracks(prev => prev.filter(t => t.id !== trackId));
    if (soloTrackId === trackId) {
      setSoloTrackId(null);
    }
  }, [soloTrackId]);
  
  // Update track property
  const updateTrack = useCallback((trackId, updates) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, ...updates } : t
    ));
  }, []);
  
  // Toggle mute
  const toggleMute = useCallback((trackId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, muted: !t.muted } : t
    ));
  }, []);
  
  // Toggle solo
  const toggleSolo = useCallback((trackId) => {
    setTracks(prev => {
      const track = prev.find(t => t.id === trackId);
      if (!track) return prev;
      
      if (soloTrackId === trackId) {
        setSoloTrackId(null);
        return prev.map(t => ({ ...t, solo: false }));
      } else {
        setSoloTrackId(trackId);
        return prev.map(t => ({ ...t, solo: t.id === trackId }));
      }
    });
  }, [soloTrackId]);
  
  // Set volume
  const setTrackVolume = useCallback((trackId, volume) => {
    updateTrack(trackId, { volume: Math.max(0, Math.min(2, volume)) });
  }, [updateTrack]);
  
  // Set pan
  const setTrackPan = useCallback((trackId, pan) => {
    updateTrack(trackId, { pan: Math.max(-1, Math.min(1, pan)) });
  }, [updateTrack]);
  
  // Mix all tracks to single buffer
  const mixdown = useCallback(() => {
    if (tracks.length === 0) return null;
    
    const activeTracks = tracks.filter(t => {
      if (soloTrackId) return t.id === soloTrackId;
      return !t.muted && t.buffer;
    });
    
    if (activeTracks.length === 0) return null;
    
    // Find longest buffer
    const maxLength = Math.max(...activeTracks.map(t => t.buffer.length));
    const sampleRate = activeTracks[0].buffer.sampleRate;
    
    const ctx = getAudioContext();
    const mixed = ctx.createBuffer(2, maxLength, sampleRate);
    
    for (const track of activeTracks) {
      const effectiveVolume = track.volume * masterVolume;
      const leftGain = effectiveVolume * (1 - Math.max(0, track.pan));
      const rightGain = effectiveVolume * (1 + Math.min(0, track.pan));
      
      for (let ch = 0; ch < 2; ch++) {
        const output = mixed.getChannelData(ch);
        const channelGain = ch === 0 ? leftGain : rightGain;
        
        const inputCh = Math.min(ch, track.buffer.numberOfChannels - 1);
        const input = track.buffer.getChannelData(inputCh);
        
        for (let i = 0; i < input.length; i++) {
          output[i] += input[i] * channelGain;
        }
      }
    }
    
    return mixed;
  }, [tracks, soloTrackId, masterVolume]);
  
  // Clear all tracks
  const clearTracks = useCallback(() => {
    setTracks([]);
    setSoloTrackId(null);
  }, []);
  
  return {
    tracks,
    soloTrackId,
    masterVolume,
    setMasterVolume,
    
    // Actions
    addTrack,
    removeTrack,
    updateTrack,
    toggleMute,
    toggleSolo,
    setTrackVolume,
    setTrackPan,
    mixdown,
    clearTracks
  };
};

// =============================================================================
// AUDIO RECORDER HOOK
// =============================================================================

/**
 * Hook for recording audio from input devices
 */
export const useAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [inputDevices, setInputDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('default');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(0);
  const timerRef = useRef(null);

  // Load input devices
  useEffect(() => {
    const loadDevices = async () => {
      try {
        // Check if mediaDevices is available
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
          console.warn('Media devices API not available');
          setInputDevices([]);
          return;
        }
        
        // Try to enumerate without permission first
        let devices = await navigator.mediaDevices.enumerateDevices();
        let inputs = devices.filter(d => d.kind === 'audioinput');
        
        // If we got devices but no labels, try requesting permission
        if (inputs.length > 0 && !inputs[0].label) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Got permission, enumerate again for labels
            stream.getTracks().forEach(track => track.stop());
            devices = await navigator.mediaDevices.enumerateDevices();
            inputs = devices.filter(d => d.kind === 'audioinput');
          } catch (permErr) {
            // Permission denied - still show devices without labels
            console.warn('Microphone permission not granted:', permErr.message);
          }
        }
        
        setInputDevices(inputs);
      } catch (err) {
        console.warn('Failed to load input devices:', err.message);
        setInputDevices([]);
      }
    };
    loadDevices();
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined }
      });
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      startTimeRef.current = Date.now();
      
      timerRef.current = setInterval(() => {
        setRecordingTime((Date.now() - startTimeRef.current) / 1000);
      }, 100);
      
    } catch (err) {
      console.error('Failed to start recording:', err);
      throw err;
    }
  }, [selectedDeviceId]);

  const stopRecording = useCallback(async () => {
    return new Promise((resolve, reject) => {
      if (!mediaRecorderRef.current) return resolve(null);
      
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const arrayBuffer = await blob.arrayBuffer();
        const ctx = getAudioContext();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        
        // Stop all tracks
        mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
        
        setIsRecording(false);
        setRecordingTime(0);
        clearInterval(timerRef.current);
        
        resolve(audioBuffer);
      };
      
      mediaRecorderRef.current.stop();
    });
  }, []);

  return {
    isRecording,
    recordingTime,
    inputDevices,
    selectedDeviceId,
    setSelectedDeviceId,
    startRecording,
    stopRecording
  };
};

export default {
  useAudioEditor,
  useAudioPlayback,
  useWaveform,
  useEffectProcessor,
  useLoopMachine,
  useBeatGenerator,
  useAudioAnalysis,
  useRegionSelection,
  useDAWKeyboardShortcuts,
  useMultitrack,
  useAudioRecorder
};
